import { useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "use-intl";
import {
  ReactFlow,
  Background,
  Controls,
  BackgroundVariant,
  type NodeTypes,
  type Node,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import StepNode from "./StepNode";
import WorkflowVirtualNode from "./WorkflowVirtualNode";
import { stepsToFlow } from "./flow.utils";
import { buildStepPositionUpdates } from "./flow-drag.utils";
import { cn } from "@/utils";
import type { WorkflowStep, ExecutionMode } from "@/types/workflow.types";
import type { WorkflowCanvasStepOverlay } from "./workflow-canvas-overlay.types";

const nodeTypes: NodeTypes = {
  stepNode: StepNode,
  virtualNode: WorkflowVirtualNode,
};

interface WorkflowFlowViewProps {
  steps: WorkflowStep[];
  executionMode: ExecutionMode;
  connectorMap?: Record<string, { name: string; subtype: string }>;
  referenceLabels?: {
    models?: Record<string, string>;
    mcpTools?: Record<string, string>;
  };
  /** Called when a step node is clicked — triggers StepConfigDrawer. */
  onNodeClick?: (step: WorkflowStep) => void;
  /** Called when drag ends with updated step positions (DAG mode only). */
  onStepPositionChange?: (
    updates: Array<{ id: string; uiPosition: { x: number; y: number } }>,
  ) => void;
  liveOverlayByStepId?: Record<string, WorkflowCanvasStepOverlay>;
  startLabel?: string;
  height?: string;
  minHeight?: string;
  className?: string;
}

export default function WorkflowFlowView({
  steps,
  executionMode,
  connectorMap,
  referenceLabels,
  onNodeClick,
  onStepPositionChange,
  liveOverlayByStepId,
  startLabel,
  height,
  minHeight = "360px",
  className,
}: WorkflowFlowViewProps) {
  const t = useTranslations("workflows");
  const { nodes: flowNodes, edges } = useMemo(
    () => stepsToFlow(steps, executionMode, connectorMap, referenceLabels, { startLabel }),
    [steps, executionMode, connectorMap, referenceLabels, startLabel],
  );

  // Local node state for optimistic drag updates (DAG mode only).
  const [nodes, setNodes] = useState(flowNodes);
  // Sync when steps prop changes (e.g. after PATCH success).
  useEffect(() => {
    setNodes(flowNodes);
  }, [flowNodes]);

  const renderedNodes = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          ...(node.type === "stepNode" ? { liveOverlay: liveOverlayByStepId?.[node.id] } : {}),
        },
      })),
    [nodes, liveOverlayByStepId],
  );

  const renderedEdges = useMemo(
    () =>
      edges.map((edge) => {
        const sourceLive = liveOverlayByStepId?.[edge.source];
        return {
          ...edge,
          animated: Boolean(sourceLive?.isActive),
          className: sourceLive?.isActive ? "workflow-live-edge" : undefined,
        };
      }),
    [edges, liveOverlayByStepId],
  );

  // Debounce ref — 500ms idle window batches multi-node drags into one PATCH
  // and absorbs xyflow's onNodeDragStop fire-per-node behavior.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPositionsRef = useRef(new Map<string, { x: number; y: number }>());

  const handleNodeDragStop = useCallback(
    (_event: React.MouseEvent, _node: Node, draggedNodes: Node[]) => {
      if (executionMode !== "dag" || !onStepPositionChange) return;

      // Optimistic local position already updated by xyflow.
      // Schedule a debounced PATCH so rapid multi-node drags coalesce.
      for (const draggedNode of draggedNodes) {
        if (draggedNode.type !== "stepNode") continue;
        pendingPositionsRef.current.set(draggedNode.id, {
          x: draggedNode.position.x,
          y: draggedNode.position.y,
        });
      }
      setNodes((currentNodes) =>
        currentNodes.map((currentNode) => {
          const pendingPosition = pendingPositionsRef.current.get(currentNode.id);
          return pendingPosition ? { ...currentNode, position: pendingPosition } : currentNode;
        }),
      );

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const updates = buildStepPositionUpdates(nodes, draggedNodes, pendingPositionsRef.current);
        pendingPositionsRef.current.clear();
        if (updates.length > 0) onStepPositionChange(updates);
      }, 500);
    },
    [executionMode, nodes, onStepPositionChange],
  );

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (!onNodeClick) return;
      const step = steps.find((s) => s.id === node.id);
      if (step) onNodeClick(step);
    },
    [onNodeClick, steps],
  );

  // Linear-mode workflows have implicit ordering; allowing drag would visually
  // contradict execution order. DAG steps own uiPosition; drag persists per-step.
  const draggable = executionMode === "dag";
  const canvasHeight =
    height ?? (steps.length <= 2 ? "360px" : "clamp(420px, calc(100vh - 360px), 620px)");

  if (steps.length === 0) {
    return (
      <div className="flex h-[400px] items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
        <p className="text-sm text-gray-500 dark:text-gray-400">{t("flow.no_steps_message")}</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "w-full overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700",
        className,
      )}
      style={{ height: canvasHeight, minHeight }}
    >
      <ReactFlow
        nodes={renderedNodes}
        edges={renderedEdges}
        nodeTypes={nodeTypes}
        nodesDraggable={draggable}
        nodesConnectable={false}
        elementsSelectable={true}
        onNodeDragStop={handleNodeDragStop}
        onNodeClick={handleNodeClick}
        fitView
        fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  );
}
